/** Creating, stepping and fingerprinting a world. This is the simulation's whole public surface. */

import type { Ctx } from './ctx';
import { createEnemy, stepEnemies } from './enemies';
import { vehicleWrecks } from './enemies/specials';
import { createHazardState, stepHazards } from './hazards';
import { spanWrecks } from './hazards/spans';
import { createInsertionState, stepInsertion } from './insertion';
import { stepMission, stepRecall } from './mission';
import { withMods } from './mods';
import { createObjectiveState, stepObjectives } from './objectives';
import { stepProjectiles } from './projectiles';
import { stepRanger } from './ranger';
import { Pcg32 } from './rng';
import { stepSmokes } from './smoke';
import { buildStatics, type Statics, type Wreck } from './statics';
import { CARBINE, STANDARD_ISSUE } from './tuning';
import {
  type Command, DT, type Enemy, type Loadout, type MissionDef, type SimEvent, type SimMods, TICK_RATE, type World,
} from './types';
import { upgradeWorld } from './upgrade';

export interface Sim {
  def: MissionDef;
  world: World;
  statics: Statics;
}

export interface SimOptions {
  /** What the ranger carries and what is in the pods; default is standard issue, no pods. */
  loadout?: Loadout;
  /** Perk and trait modifiers of the deployed ranger. */
  mods?: Partial<SimMods>;
  /** Start on the ground at `def.player` (tests, practice restarts) instead of in the aircraft. Default true. */
  skipInsertion?: boolean;
}

/**
 * R4: what blocks the ground without being a prop - burnt-out vehicles and the rubble of a fallen span.
 * Rebuilt with the rest of the statics, so a restored save gets exactly the grid it had.
 */
function wrecksOf(def: MissionDef, world: World): Wreck[] {
  return [...vehicleWrecks(world.enemies), ...spanWrecks(def, world.props)];
}

export const NEUTRAL_COMMAND: Command = {
  moveX: 0, moveZ: 0, aim: Math.PI, aimDist: 10,
  run: false, crawl: false, sneak: false, fire: false, use: false,
  select: null, reload: false, firstAid: false, interact: false, recall: false, fuse: 10, aimAssist: 0,
};

/**
 * ROUND8: a dog knows its handler by the id he was actually given. Ids count only the spawns created at the start,
 * so where a dormant spawn comes before him, 1000 + his spawn index named the wrong man until the dog's own first
 * step looked him up (dog.ts `bindHandler`, which still does it for a handler who arrives later).
 */
function bindHandlers(def: MissionDef, enemies: Enemy[]): void {
  const idOf = new Map<number, number>();
  let next = 1000;
  def.spawns.forEach((s, i) => {
    if (s.dormantUntil === undefined) idOf.set(i, next++);
  });
  for (const e of enemies) {
    const index = e.dog?.handlerSpawn;
    const id = index === undefined ? undefined : idOf.get(index);
    if (e.dog === undefined || id === undefined) continue;
    e.handlerId = id;
    delete e.dog.handlerSpawn;
  }
}

export function createSim(def: MissionDef, options: SimOptions = {}): Sim {
  const rng = new Pcg32(def.seed, 7);
  // Mission 32 starts in a cell: no aircraft, no canopy, empty pouches and the knife (10 §2).
  const captured = def.startCaptured === true;
  const skip = captured || options.skipInsertion !== false;
  const emptyHanded = { magazines: 0, roundsInMag: 0, grenades: 0, laws: 0, bombs: 0, firstAid: 0 };
  // Dormant spawns (minitanks, the hostage executioners) are created later by their trigger.
  const enemies = def.spawns
    .filter((s) => s.dormantUntil === undefined)
    .map((spawn, i) => createEnemy(1000 + i, spawn, i, rng.float(), rng.float(), def.threat));
  bindHandlers(def, enemies);
  const world: World = {
    tick: 0,
    smokes: [],
    rng: rng.getState(),
    nextId: 2000,
    clockTicks: def.clockSeconds * TICK_RATE,
    ranger: {
      x: def.player.x, z: def.player.z, prevX: def.player.x, prevZ: def.player.z,
      facing: def.player.facing, aim: def.player.facing,
      backpedal: false, stance: 'stand', running: false, moving: false, inTrench: false, travelled: 0,
      wounds: 0, fatigue: 0, winded: false, status: 'active', hurtBy: 'none',
      gear: { smoke: 0, pistolRounds: 0, nvg: false, wireCutters: false, ...(captured ? {} : options.loadout?.gear) },
      inventory: captured ? emptyHanded : { ...(options.loadout?.carried ?? STANDARD_ISSUE) },
      weapon: captured ? 'knife' : 'carbine',
      fireCooldown: 0, fireCarry: 0, reloadTicks: 0, staggerTicks: 0, throwCharge: 0, burstRounds: 0, raiseTicks: 0,
      actionTicks: 0, action: 'none', recentHits: [], bleedTicks: 0, sinceShot: 999,
      kills: 0, knifeKills: 0,
      prevFire: false, prevUse: false, prevInteract: false, prevFirstAid: false, prevRecall: false, prevReload: false,
      captureTicks: 0, footstepTicks: 0, dragging: -1,
    },
    enemies,
    bullets: [], thrown: [], rockets: [], bombs: [],
    props: def.props.map((p) => ({ id: p.id, destroyed: false, looted: false })),
    pendingBlasts: [],
    runnerId: -1,
    alert: def.startAlert, alertQuietTicks: 0, combatTicks: 0,
    reinforcementWaves: 0, reinforcementTicks: 0,
    disturbanceX: def.player.x, disturbanceZ: def.player.z,
    osprey: { phase: 'away', ticks: 0, recalled: false },
    stranded: false,
    targetsDestroyed: 0,
    targetsTotal: Math.max(1, def.objective.targetPropIds.length),
    emplacementsDestroyed: 0,
    maxAlert: def.startAlert,
    result: null,
    objective: createObjectiveState(def),
    hazards: createHazardState(def),
    insertion: createInsertionState(def, options.loadout, skip),
    mods: withMods(options.mods),
  };
  // The objective knows how many things there are to do (captives, halves of a radar array); the
  // target count alone is wrong for those missions until the first ground tick would correct it.
  world.targetsTotal = Math.max(1, world.objective.total);
  return { def, world, statics: buildStatics(def, world.props, 0, wrecksOf(def, world)) };
}

/** Rebuild a Sim around a deserialised world (suspend saves, replays, tests). */
export function restoreSim(def: MissionDef, world: World): Sim {
  upgradeWorld(world);
  return { def, world, statics: buildStatics(def, world.props, 0, wrecksOf(def, world)) };
}

/** Advance one fixed tick. Returns this tick's one-shot events for presentation. */
export function stepSim(sim: Sim, cmd: Command): SimEvent[] {
  const events: SimEvent[] = [];
  const w = sim.world;
  if (w.result) return events;
  const rng = Pcg32.fromState(w.rng);
  const ctx: Ctx = { world: w, statics: sim.statics, rng, events, cmd, staticsDirty: false };

  if (w.insertion.phase !== 'ground') {
    // In the air: the garrison goes about its day, the clock has not started.
    stepInsertion(ctx);
    stepEnemies(ctx);
    stepProjectiles(ctx, DT, CARBINE.speed);
  } else {
    stepRecall(ctx);
    stepRanger(ctx);
    stepHazards(ctx);
    stepSmokes(ctx);
    stepEnemies(ctx);
    stepProjectiles(ctx, DT, CARBINE.speed);
    stepObjectives(ctx);
  }
  stepMission(ctx);

  if (ctx.staticsDirty) sim.statics = buildStatics(sim.def, w.props, sim.statics.version + 1, wrecksOf(sim.def, w));
  w.rng = rng.getState();
  w.tick++;
  return events;
}

/** FNV-1a over the JSON of the world: the determinism and replay tests compare these. */
export function hashWorld(world: World): string {
  const text = JSON.stringify(world);
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, '0');
}
